import * as React from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'
import type { MentionSuggestion } from '@/components/agents/promptMentionsShared'

type MentionAnchor = {
  left: number
  top: number
  containerWidth?: number
  containerHeight?: number
}

type PromptMentionDropdownProps = {
  open: boolean
  anchor: MentionAnchor | null
  suggestions: MentionSuggestion[]
  selectedIndex: number
  onSelect: (suggestion: MentionSuggestion) => void
  helpText: string
  systemLabel: string
  toolLabel: string
  tagLabel?: string
  documentLabel?: string
  variableLabel?: string
  disablePortal?: boolean
  searchValue?: string
  onSearchChange?: (value: string) => void
  searchInputRef?: React.RefObject<HTMLInputElement | null>
  filterPlaceholder?: string
}

const MENU_WIDTH = 360
const MENU_MAX_HEIGHT = 280
const VIEWPORT_MARGIN = 8

export function PromptMentionDropdown({
  open,
  anchor,
  suggestions,
  selectedIndex,
  onSelect,
  helpText,
  systemLabel,
  toolLabel,
  tagLabel = 'Tags',
  documentLabel = 'Documents',
  variableLabel = 'Variables',
  disablePortal = false,
  searchValue = '',
  onSearchChange,
  searchInputRef,
  filterPlaceholder = 'Filter',
}: PromptMentionDropdownProps) {
  if (!open || !anchor || suggestions.length === 0) return null

  const systemSuggestions = suggestions.filter((item) => item.category === 'system')
  const variableSuggestions = suggestions.filter((item) => item.category === 'variable')
  const toolSuggestions = suggestions.filter((item) => item.category === 'tool')
  const tagSuggestions = suggestions.filter((item) => item.category === 'tag')
  const documentSuggestions = suggestions.filter((item) => item.category === 'document')
  const useViewportPositioning = !disablePortal
  const availableWidth = anchor.containerWidth ?? window.innerWidth
  const availableHeight = anchor.containerHeight ?? window.innerHeight
  const clampedLeft = useViewportPositioning
    ? Math.max(
        VIEWPORT_MARGIN,
        Math.min(anchor.left, window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN)
      )
    : Math.max(
        VIEWPORT_MARGIN,
        Math.min(anchor.left, availableWidth - MENU_WIDTH - VIEWPORT_MARGIN)
      )
  const shouldOpenUp = useViewportPositioning
    ? anchor.top + MENU_MAX_HEIGHT > window.innerHeight - VIEWPORT_MARGIN
    : anchor.top + MENU_MAX_HEIGHT > availableHeight - VIEWPORT_MARGIN
  const rawTop = shouldOpenUp
    ? Math.max(VIEWPORT_MARGIN, anchor.top - MENU_MAX_HEIGHT - 8)
    : anchor.top
  const menuTop = useViewportPositioning
    ? rawTop
    : Math.max(
        VIEWPORT_MARGIN,
        Math.min(rawTop, Math.max(VIEWPORT_MARGIN, availableHeight - MENU_MAX_HEIGHT - VIEWPORT_MARGIN))
      )

  let indexOffset = 0
  const content = (
    <div
      data-prompt-mention-dropdown="true"
      className={cn(
        'pointer-events-auto z-[120] w-[min(360px,calc(100vw-1rem))] rounded-lg border bg-background shadow-md',
        useViewportPositioning ? 'fixed' : 'absolute'
      )}
      style={{ left: clampedLeft, top: menuTop }}
    >
      <p className="border-b border-border px-3 py-2 text-[11px] text-muted-foreground">{helpText}</p>
      {onSearchChange ? (
        <div className="border-b border-border px-2 py-2">
          <Input
            ref={searchInputRef}
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={filterPlaceholder}
            className="h-8 text-xs"
          />
        </div>
      ) : null}
      <div className="max-h-[248px] overflow-auto p-2">
        {([
          { key: 'system', label: systemLabel, items: systemSuggestions },
          { key: 'variable', label: variableLabel, items: variableSuggestions },
          { key: 'tool', label: toolLabel, items: toolSuggestions },
          { key: 'tag', label: tagLabel, items: tagSuggestions },
          { key: 'document', label: documentLabel, items: documentSuggestions },
        ] as const).map((section) => {
          if (section.items.length === 0) return null
          const sectionStart = indexOffset
          indexOffset += section.items.length

          return (
            <div key={section.key} className="mb-2 last:mb-0">
              <p className="mb-1 px-1 text-[11px] font-medium uppercase text-muted-foreground">
                {section.label}
              </p>
              <div className="space-y-1">
                {section.items.map((suggestion, index) => {
                  const absoluteIndex = sectionStart + index
                  const isSelected = selectedIndex === absoluteIndex
                  return (
                    <Button
                      key={`${section.key}-${suggestion.token}-${index}`}
                      variant="ghost"
                      size="xs"
                      className={cn(
                        'w-full justify-between text-left',
                        isSelected && 'bg-muted text-foreground'
                      )}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => onSelect(suggestion)}
                    >
                      <span className="font-mono text-[11px]">{suggestion.label || suggestion.token}</span>
                      {suggestion.description ? (
                        <span className="ml-2 text-right text-[10px] text-muted-foreground">
                          {suggestion.description}
                        </span>
                      ) : null}
                    </Button>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )

  if (disablePortal) return content
  return createPortal(content, document.body)
}
